import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  logout,
  saveLastRoute,
  setPendingReauthIdentity,
  showLoginModal,
} from "./authSlice";
import { AUTH_EVENTS, postAuthEvent } from "../../utils/authChannel";
import { CSRF_HEADER, readCsrfToken } from "../../utils/csrf";
import { withRefreshLock } from "./refreshLock";

export const REFRESH_URL = "api/user/token/refresh/";

/* REAL baseQuery (function) No Authorization header. */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: "/",
  credentials: "include",

  prepareHeaders: (headers) => {
    // Double-submit CSRF. Safe methods do not need it and the server does not
    // ask for one, but sending it unconditionally keeps this simple and costs
    const csrf = readCsrfToken();
    if (csrf) headers.set(CSRF_HEADER, csrf);

    return headers;
  },
});

/**
 * Wrapper
 */
export const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  // Signing OUT with a token the server already rejects answers 401. That is
  // the expected outcome of the request, not a session to rescue — prompting
  // for re-authentication there pops "Login Required" over somebody who just
  // asked to leave, and the modal cannot be dismissed into a working session.
  const url = typeof args === "string" ? args : args?.url ?? "";
  const isLogoutCall = url.includes("api/user/logout");
  const isRefreshCall = url.includes(REFRESH_URL);
  const isSessionProbe = url.includes("api/user/session");

  if (result?.error?.status !== 401 || isLogoutCall || isRefreshCall) {
    return result;
  }

  // A 401 is no longer proof the session is over — the access token may simply
  // have aged out while the refresh cookie is fine. Try a refresh first, once
  // per browser, and replay the original request if it worked.
  const refreshed = await withRefreshLock(async () => {
    const response = await rawBaseQuery(
      { url: REFRESH_URL, method: "POST" },
      api,
      extraOptions
    );
    return !response?.error;
  });

  if (refreshed) {
    // The new cookies are already set by the response; the retry just needs to
    // be sent. Tabs that were waiting on the lock land here too.
    postAuthEvent(AUTH_EVENTS.REFRESHED);
    result = await rawBaseQuery(args, api, extraOptions);

    if (result?.error?.status !== 401) return result;
  }

  // The session probe is how a new tab asks "am I signed in?". A 401 there is
  // an ANSWER, not a failure, so it must not raise a modal over a visitor who
  // was never signed in to begin with.
  if (isSessionProbe) return result;

  const state = api.getState();

  if (!state.auth.showLoginModal) {
    api.dispatch(saveLastRoute(window.location.pathname));
    // TIP-511 — pin the modal to the account whose session just died. The
    // store is deliberately NOT cleared here: an expiry mid-checkout must
    // keep its in-progress state. The cross-user leak is closed instead at
    // re-login, where a changed identity purges identity-scoped storage.
    api.dispatch(setPendingReauthIdentity(state.auth?.userInfo?.email ?? null));
    api.dispatch(showLoginModal());
  }

  return result;
};
